// utils/required/required.js
//
// Which elements a row MUST carry, and whether it does.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. Vendored verbatim into
// keepr-web (`src/utils/required/required.js`) and kept in lock-step by the
// shared vector file beside it (`vectors.json`), which both repos' suites
// drive. Nothing here may require() anything: the same answer has to come
// out of a browser typing into a form and a server validating a batch, and
// the only way to guarantee that is one file.
//
// Two rules, one evaluator:
//
//   options.required        a flat boolean, as it always was
//   options.requiredWhen    [{ element, op, value }] — required only while
//                           every clause holds
//
// The second exists because "reason for variance", "why late", "damage
// notes", "cancellation reason" and "other — specify" are all the same
// shape: a field that is noise on the ordinary row and the whole point of
// the exceptional one. Asking for it always trains people to type a dot;
// asking for it never loses the one entry that mattered.
//
// A clause names an element on the SAME row. Cross-row conditions are a
// query, not a requirement, and belong to KQL.

'use strict';

// The comparison vocabulary. Deliberately small and total: every operator
// answers true or false for every pair of values, so a requirement can never
// be indeterminate at save time.
const OPS = ['eq', 'ne', 'in', 'not-in', 'empty', 'not-empty', 'gt', 'lt'];

const isBlank = value => value === undefined
	|| value === null
	|| value === ''
	|| (Array.isArray(value) && value.length === 0)
	// A measurement stores { value, unit, base }; an empty one is the object
	// with nothing in it, which is what a cleared field leaves behind.
	|| (typeof value === 'object' && !Array.isArray(value) && value !== null
		&& Object.keys(value).length === 0);

const toComparable = (value) => {
	if (value === null || value === undefined) return '';
	// A measurement compares by its canonical magnitude, which is the only
	// number two measurements of the same measure can be compared on.
	if (typeof value === 'object' && !Array.isArray(value) && typeof value.base === 'number') return value.base;
	if (typeof value === 'boolean') return value ? 'true' : 'false';
	return value;
};

// Money stores { amount, currency }: an amount is a number only IN its
// currency (no rates — keepr-web docs/plans/currency.md), and this module
// cannot know an exponent, so a clause about a currency element names money
// too — `{ element: 'total', op: 'gt', value: { amount: 50000, currency:
// 'USD' } }` — and two amounts in different currencies are neither equal nor
// ordered. A bare number against money is never true: it could be dollars
// or cents.
const isMoney = value => typeof value === 'object' && value !== null && !Array.isArray(value)
	&& typeof value.amount === 'number' && typeof value.currency === 'string';

const moneyCompare = (a, b) => (isMoney(a) && isMoney(b) && a.currency === b.currency ? a.amount - b.amount : NaN);

const sameValue = (a, b) => {
	if (isMoney(a) || isMoney(b)) return moneyCompare(a, b) === 0;
	const left = toComparable(a);
	const right = toComparable(b);
	if (typeof left === 'number' || typeof right === 'number') return Number(left) === Number(right);
	return String(left) === String(right);
};

/** Does one clause hold for this row? An unknown operator never requires. */
function clauseHolds(clause, elements) {
	if (!clause || typeof clause !== 'object') return false;
	const actual = elements ? elements[clause.element] : undefined;
	const expected = clause.value;
	switch (clause.op) {
		case 'empty': return isBlank(actual);
		case 'not-empty': return !isBlank(actual);
		case 'eq':
			return Array.isArray(actual) ? actual.some(entry => sameValue(entry, expected)) : sameValue(actual, expected);
		case 'ne':
			return !(Array.isArray(actual) ? actual.some(entry => sameValue(entry, expected)) : sameValue(actual, expected));
		case 'in':
			return Array.isArray(expected) && expected.some(candidate => (Array.isArray(actual)
				? actual.some(entry => sameValue(entry, candidate))
				: sameValue(actual, candidate)));
		case 'not-in':
			return Array.isArray(expected) && !expected.some(candidate => (Array.isArray(actual)
				? actual.some(entry => sameValue(entry, candidate))
				: sameValue(actual, candidate)));
		case 'gt':
			if (isMoney(actual) || isMoney(expected)) return moneyCompare(actual, expected) > 0;
			return Number(toComparable(actual)) > Number(toComparable(expected));
		case 'lt':
			if (isMoney(actual) || isMoney(expected)) return moneyCompare(actual, expected) < 0;
			return Number(toComparable(actual)) < Number(toComparable(expected));
		default: return false;
	}
}

/**
 * Is this element required for this row? `options.required` is
 * unconditional; `options.requiredWhen` requires only while EVERY clause
 * holds (clauses are ANDed — an OR is two rules, and the one people mean is
 * almost always AND).
 */
function isRequiredFor(element, elements) {
	const options = (element && element.options) || element || {};
	if (options.required === true) return true;
	const clauses = options.requiredWhen;
	if (!Array.isArray(clauses) || clauses.length === 0) return false;
	return clauses.every(clause => clauseHolds(clause, elements));
}

/**
 * Every element of `elementDefs` that this row must carry and does not.
 * Returns `[{ element, message }]`, empty when the row is complete.
 *
 * `elementDefs` is the FLATTENED (inherited) element list: an inherited
 * element is required exactly like a declared one.
 *
 * `context.attachments === false` says the row's collection has its
 * attachments switched off: a `file` element is then never required, since
 * the platform refuses the only thing that could fill it (keepr-web
 * docs/plans/data-types-file-element.md §5). Absent, it is asked as any
 * element is.
 */
function missingRequired(elementDefs, elements, context) {
	const attachmentsOff = !!context && context.attachments === false;
	const out = [];
	(elementDefs || []).forEach(def => {
		if (!def || !def.name) return;
		// Driven values are system-owned: requiring one would refuse a row for
		// a value the person cannot type. A SEQUENCE element is system-owned
		// for the same reason and skipped for the same reason, and here the
		// skip is load-bearing rather than merely kind: missingRequired runs
		// on the RAW payload BEFORE the create path's element chain, so a
		// required sequence element would 400 with `required` on every create
		// before the allocator ever got to issue the number.
		const options = def.options || {};
		if (options.drivenFrom) return;
		if (options.sequence) return;
		if (attachmentsOff && def.dataType === 'file') return;
		if (!isRequiredFor(def, elements)) return;
		if (!isBlank(elements ? elements[def.name] : undefined)) return;
		const label = (def.label && def.label.singular) || def.name;
		out.push({ element: def.name, message: `${label} is required.` });
	});
	return out;
}

/** Write-time validation of a `requiredWhen` config. Returns an error string
 * or null — the caller decides which 400 shape to throw. */
function validateRequiredWhen(clauses, elementNames) {
	if (clauses === undefined || clauses === null) return null;
	if (!Array.isArray(clauses) || clauses.length === 0) return 'requiredWhen must be a non-empty list of conditions.';
	if (clauses.length > 5) return 'requiredWhen takes at most 5 conditions.';
	for (const clause of clauses) {
		if (!clause || typeof clause !== 'object') return 'every requiredWhen condition must be an object.';
		if (!clause.element || typeof clause.element !== 'string') return 'every requiredWhen condition needs an element.';
		if (elementNames && !elementNames.includes(clause.element)) {
			return `requiredWhen names "${clause.element}", which is not an element of this card.`;
		}
		if (!OPS.includes(clause.op)) return `requiredWhen op must be one of: ${OPS.join(', ')}.`;
		if ((clause.op === 'in' || clause.op === 'not-in') && !Array.isArray(clause.value)) {
			return `requiredWhen "${clause.op}" needs a list of values.`;
		}
	}
	return null;
}

const api = { OPS, isRequiredFor, missingRequired, validateRequiredWhen, _clauseHolds: clauseHolds, _isBlank: isBlank };

if (typeof module !== 'undefined' && module.exports) module.exports = api;
